import { usePrintStore, type PrintMethod, type HotFolderConfig } from '@renderer/stores/printStore'
import printJS from 'print-js'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { isInTauriShell } from '@renderer/lib/runtime'
import { printImage } from '@renderer/lib/runtime/print'
// Types for printer capabilities (matching main process)
export interface PrinterCaps {
  printerName: string
  defaults: {
    orientation?: 'portrait' | 'landscape' | string | null
    pageSize?: { name?: string | null; widthMicrons: number; heightMicrons: number } | null
    dpi?: { x: number; y: number } | null
  }
  capabilities: {
    pageSizes?: Array<{ name?: string | null; widthMicrons: number; heightMicrons: number }>
    dpis?: Array<{ x: number; y: number }>
    supportsDuplex?: boolean
    supportsCollate?: boolean
    printableAreaMicrons?: {
      originMicrons: { x: number; y: number }
      extentMicrons: { widthMicrons: number; heightMicrons: number }
    } | null
  }
}

interface PrintResult {
  success: boolean
  error?: string
  failureReason?: string
}

interface PrintOptions {
  printerName?: string
  copies?: number
  orientation?: 'landscape' | 'portrait'
  pageSize?: {
    width: number
    height: number
  }
  margins?: {
    marginType: 'default' | 'none' | 'printableArea' | 'custom'
    top?: number
    bottom?: number
    left?: number
    right?: number
  }
  dpi?: {
    horizontal: number
    vertical: number
  }
  scaleFactor?: number
  printBackground?: boolean
  silent?: boolean
  printResolutionScale?: number
  printerType?: 'default' | 'secondary' | 'tertiary'
  useSumatraPDF?: boolean
  printMethod?: PrintMethod
  hotFolderConfig?: HotFolderConfig
  printPositionShift?: {
    top?: number
    bottom?: number
    left?: number
    right?: number
  }

  duplicateCheckId?: string
}

export class PrintUtils {
  // Track print jobs to prevent duplicates
  private static activePrintJobs = new Set<string>()
  private static printJobHistory = new Map<string, number>()

  /**
   * Get printer capabilities from main process
   */
  static async getPrinterCapabilities(printerName?: string): Promise<PrinterCaps | null> {
    if (typeof window.api === 'undefined' || !window.api.getPrinterCapabilities) {
      console.warn('Printer capabilities API not available')
      return null
    }

    try {
      return await window.api.getPrinterCapabilities(printerName)
    } catch (error) {
      console.error('Failed to get printer capabilities:', error)
      return null
    }
  }

  /**
   * Convert microns to common units for display
   */
  static micronsToMm(microns: number): number {
    return Math.round(microns / 1000)
  }

  static micronsToInches(microns: number): number {
    return Math.round((microns / 25400) * 100) / 100
  }

  /**
   * Format page size for display
   */
  static formatPageSize(pageSize: {
    widthMicrons: number
    heightMicrons: number
    name?: string | null
  }): string {
    const widthMm = PrintUtils.micronsToMm(pageSize.widthMicrons)
    const heightMm = PrintUtils.micronsToMm(pageSize.heightMicrons)
    const widthIn = PrintUtils.micronsToInches(pageSize.widthMicrons)
    const heightIn = PrintUtils.micronsToInches(pageSize.heightMicrons)

    const sizeStr = `${widthMm}×${heightMm}mm (${widthIn}×${heightIn}")`
    return pageSize.name ? `${pageSize.name} - ${sizeStr}` : sizeStr
  }

  static async downloadImage(url: string, filename: string): Promise<void> {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
  }

  /** Convert image URL (data URL or blob URL) to base64 for Tauri print_job. */
  private static async imageUrlToBase64(imageUrl: string): Promise<string | null> {
    if (imageUrl.startsWith('data:')) {
      const comma = imageUrl.indexOf(',')
      return comma >= 0 ? imageUrl.slice(comma + 1) : null
    }
    if (imageUrl.startsWith('blob:')) {
      try {
        const res = await fetch(imageUrl)
        const blob = await res.blob()
        return await new Promise<string | null>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (): void => {
            const dataUrl = reader.result as string
            const comma = dataUrl.indexOf(',')
            resolve(comma >= 0 ? dataUrl.slice(comma + 1) : null)
          }
          reader.onerror = (): void => reject(reader.error)
          reader.readAsDataURL(blob)
        })
      } catch {
        return null
      }
    }
    return null
  }

  private static async fallbackToPrintJS(
    imageUrl: string
  ): Promise<{ success: boolean; error?: string; failureReason?: string }> {
    console.log('Falling back to Print.js for printing')
    return new Promise((resolve) => {
      try {
        printJS({
          printable: imageUrl,
          type: 'image',
          showModal: true,
          style: `
          @media print {
            @page { margin: 0; size: auto; }
            body, html { margin: 1; padding: 1; }
            img { margin: 0; padding: 0; width: 100%; object-fit: contain; }
            }
          `,
          onPrintDialogClose: () => {
            console.log('Print dialog closed')
            resolve({ success: true })
          },
          onError: (error: Error) => {
            console.error('Print.js error:', error)
            resolve({ success: false, error: 'Print.js failed', failureReason: error.message })
          }
        })
      } catch (error) {
        console.error('Print.js initialization failed:', error)
        resolve({
          success: false,
          error: 'Print.js unavailable',
          failureReason: error instanceof Error ? error.message : 'Unknown error'
        })
      }
    })
  }

  static async sendToPrinter(
    imageUrl: string,
    options: PrintOptions = {}
  ): Promise<{ success: boolean; error?: string; failureReason?: string }> {
    const { logActivity, id: sessionId } = useSessionStateStore.getState()

    // Create unique print job identifier
    const printJobId = `${sessionId}-${Date.now()}-${imageUrl.substring(0, 50)}`
    const duplicateCheckId =
      options.duplicateCheckId || `${sessionId}-${imageUrl.substring(0, 100)}`

    // Check for duplicate print jobs within the last 5 seconds
    const now = Date.now()
    const lastPrintTime = PrintUtils.printJobHistory.get(duplicateCheckId)
    if (lastPrintTime && now - lastPrintTime < 5000) {
      return {
        success: false,
        error: 'Duplicate print job prevented',
        failureReason: 'Print job sent too recently'
      }
    }

    // Check if this print job is already active
    if (PrintUtils.activePrintJobs.has(duplicateCheckId)) {
      return {
        success: false,
        error: 'Print job already in progress',
        failureReason: 'Another print job for this content is already processing'
      }
    }

    // Mark this print job as active
    PrintUtils.activePrintJobs.add(duplicateCheckId)
    PrintUtils.printJobHistory.set(duplicateCheckId, now)

    // Check for remote printer selection first
    const printStore = usePrintStore.getState()
    const selectedRemote = printStore.getSelectedRemotePrinter()
    if (selectedRemote && printStore.selectedPrinterTarget === 'remote') {
      try {
        const base64 = await PrintUtils.imageUrlToBase64(imageUrl)
        if (!base64) {
          PrintUtils.activePrintJobs.delete(duplicateCheckId)
          return { success: false, error: 'Could not get image data', failureReason: 'Invalid image URL' }
        }

        // Prefer a per-session web override if the user set one; otherwise fall back to the
        // settings the remote booth registered (its own calibration), then to local defaults.
        const remoteSettings =
          printStore.remotePrintOverride || selectedRemote.printSettings || printStore.settings
        const printData = {
          images: [`data:image/png;base64,${base64}`],
          printerName: selectedRemote.printerName,
          pageSize: remoteSettings.pageSize,
          orientation: remoteSettings.orientation,
          margins: remoteSettings.margins,
          dpi: remoteSettings.dpi,
          scaleFactor: remoteSettings.scaleFactor,
          printBackground: remoteSettings.printBackground,
          printResolutionScale: remoteSettings.printResolutionScale,
          printPositionShift: remoteSettings.printPositionShift
        }

        const response = await fetch(`${selectedRemote.tunnelUrl}/print`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requestId: printJobId, data: printData })
        })

        if (!response.ok) {
          throw new Error(`Remote print endpoint returned ${response.status}`)
        }

        await response.json()
        logActivity('Remote print job sent successfully', {
          printerName: selectedRemote.printerName,
          machineIdentifier: selectedRemote.machineIdentifier,
          printJobId
        })

        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return { success: true }
      } catch (error) {
        console.error('Remote print failed:', error)
        logActivity('Remote print failed', {
          printJobId,
          error: error instanceof Error ? error.message : 'Unknown error',
          level: 'warning'
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return {
          success: false,
          error: 'Remote print failed',
          failureReason: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }

    // Tauri shell: use native bridge (print_job command)
    if (isInTauriShell()) {
      try {
        const base64 = await PrintUtils.imageUrlToBase64(imageUrl)
        if (!base64) {
          PrintUtils.activePrintJobs.delete(duplicateCheckId)
          return { success: false, error: 'Could not get image data', failureReason: 'Invalid image URL' }
        }
        const { settings, selectedPrinter } = usePrintStore.getState()
        // Store and callers use pageSize in microns; Tauri expects mm
        const pageSizeMicrons = options.pageSize ?? settings.pageSize
        const paperSizeMm =
          pageSizeMicrons && pageSizeMicrons.width != null && pageSizeMicrons.height != null
            ? {
                width: pageSizeMicrons.width / 1000,
                height: pageSizeMicrons.height / 1000,
                unit: 'mm' as const
              }
            : undefined
        const result = await printImage({
          jobId: printJobId,
          imageData: base64,
          printerName: options.printerName || selectedPrinter || undefined,
          copies: options.copies ?? 1,
          orientation: options.orientation ?? settings.orientation ?? 'portrait',
          paperSize: paperSizeMm,
          dpi: options.dpi ? (options.dpi.horizontal + options.dpi.vertical) / 2 : undefined,
          scaleFit: 'fit',
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return {
          success: result.success,
          error: result.error,
          failureReason: result.errorCode ?? result.error,
        }
      } catch (err) {
        console.error('Tauri print failed:', err)
        logActivity('Tauri print failed', {
          printJobId,
          error: err instanceof Error ? err.message : String(err),
          level: 'error',
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        // Fall through to browser fallback (print-js) which may not work in Tauri; UI will show error
      }
    }

    // Electron: window.api.print
    if (typeof window.api !== 'undefined' && window.api.print) {
      try {
        const {
          settings,
          selectedPrinter,
          useSumatraPDF,
          secondaryUseSumatraPDF,
          tertiaryUseSumatraPDF,
          secondaryPrinter,
          tertiaryPrinter
        } = usePrintStore.getState()

        // Determine printer type and SumatraPDF flag
        const printerName = options.printerName || selectedPrinter || 'DS-RX1'
        let printerType: 'default' | 'secondary' | 'tertiary' = options.printerType || 'default'
        let useSumatraPDFFlag = options.useSumatraPDF

        // If printerType not explicitly provided, determine it from printer name
        if (!options.printerType) {
          if (tertiaryPrinter?.printerName === printerName) {
            printerType = 'tertiary'
          } else if (secondaryPrinter?.printerName === printerName) {
            printerType = 'secondary'
          } else {
            printerType = 'default'
          }
        }

        // If useSumatraPDF not explicitly provided, get it from store based on printer type
        if (useSumatraPDFFlag === undefined) {
          if (printerType === 'tertiary') {
            useSumatraPDFFlag = tertiaryUseSumatraPDF
          } else if (printerType === 'secondary') {
            useSumatraPDFFlag = secondaryUseSumatraPDF
          } else {
            useSumatraPDFFlag = useSumatraPDF
          }
        }

        // Resolve the print method + hot-folder config for this printer slot.
        const store = usePrintStore.getState()
        const printMethod: PrintMethod =
          options.printMethod ??
          store.getPrintMethod(printerType) ??
          (useSumatraPDFFlag ? 'sumatra' : 'electron')
        const hotFolderConfig = options.hotFolderConfig ?? store.getHotFolderConfig(printerType)

        // Get the correct settings based on printer type
        const activeSettings = printerType === 'tertiary'
          ? tertiaryPrinter?.settings || settings
          : printerType === 'secondary'
            ? secondaryPrinter?.settings || settings
            : settings

        // The hot-folder path runs in the main process and cannot fetch a
        // renderer-scoped blob: URL, so convert the image to a base64 data URL
        // before sending. Other methods forward the URL as-is (print.html loads it).
        let imagesForJob = [imageUrl]
        if (printMethod === 'hotFolder') {
          const base64 = await PrintUtils.imageUrlToBase64(imageUrl)
          if (!base64) {
            PrintUtils.activePrintJobs.delete(duplicateCheckId)
            return {
              success: false,
              error: 'Could not get image data',
              failureReason: 'Invalid image URL for hot-folder print'
            }
          }
          const mime = imageUrl.startsWith('data:')
            ? imageUrl.slice(5, imageUrl.indexOf(';'))
            : 'image/jpeg'
          imagesForJob = [`data:${mime};base64,${base64}`]
        }

        const printOptions = {
          printerName,
          copies: options.copies ?? 1,
          images: imagesForJob,
          orientation: options.orientation ?? activeSettings.orientation,
          pageSize: options.pageSize ?? activeSettings.pageSize,
          margins: options.margins ?? activeSettings.margins,
          dpi: options.dpi ?? activeSettings.dpi,
          scaleFactor: options.scaleFactor ?? activeSettings.scaleFactor ?? 100,
          printBackground: options.printBackground ?? activeSettings.printBackground ?? true,
          silent: options.silent ?? true,
          printResolutionScale: options.printResolutionScale ?? activeSettings.printResolutionScale ?? 1,
          printerType,
          useSumatraPDF: useSumatraPDFFlag,
          printMethod,
          hotFolderConfig,
          printPositionShift: options.printPositionShift ?? activeSettings.printPositionShift
        }

        // Log complete print configuration before sending to main process
        console.log('=== Print Configuration (Before Main Process) ===')
        console.log('Printer:', printOptions.printerName)
        console.log('Copies:', printOptions.copies)
        console.log('Page Size:', printOptions.pageSize)
        console.log('Orientation:', printOptions.orientation)
        console.log('Margins:', printOptions.margins)
        console.log('DPI:', printOptions.dpi)
        console.log('Scale Factor:', printOptions.scaleFactor)
        console.log('Print Background:', printOptions.printBackground)
        console.log('Silent:', printOptions.silent)
        console.log('Print Resolution Scale:', printOptions.printResolutionScale)
        console.log('===============================================')

        const result: PrintResult = await window.api.print(printOptions)

        if (result.success) {
          logActivity('Print job sent successfully', {
            printerName: printOptions.printerName,
            copies: printOptions.copies,
            pageSize: printOptions.pageSize,
            orientation: printOptions.orientation,
            margins: printOptions.margins,
            dpi: printOptions.dpi,
            scaleFactor: printOptions.scaleFactor,
            printBackground: printOptions.printBackground,
            printResolutionScale: printOptions.printResolutionScale
          })
        } else {
          logActivity('API-based print not activated', {
            printJobId,
            error: result.error,
            failureReason: result.failureReason,
            level: 'info'
          })
        }

        // Clean up active print job tracking
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return result
      } catch (error) {
        console.error('Electron API print failed:', error)
        logActivity('Electron API print failed with exception', {
          printJobId,
          error: error instanceof Error ? error.message : 'Unknown error',
          level: 'error'
        })
        // Clean up active print job tracking on error
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
      }
    }

    // Fallback to Print.js
    const fallbackResult: PrintResult = await PrintUtils.fallbackToPrintJS(imageUrl)
    if (!fallbackResult.success) {
      logActivity('Print fallback not successful', {
        printJobId,
        error: fallbackResult.error,
        failureReason: fallbackResult.failureReason,
        level: 'warning'
      })
    }

    // Clean up active print job tracking
    PrintUtils.activePrintJobs.delete(duplicateCheckId)
    return fallbackResult
  }
}
